import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  ClockCounterClockwiseIcon, CpuIcon, DiamondsFourIcon, FolderIcon, GearIcon, HouseIcon, PlugsConnectedIcon, RobotIcon,
  ShieldCheckIcon, UsersThreeIcon,
} from '@phosphor-icons/react';
import { Tooltip } from 'antd';
import {
  AppSwitcher, DocumentationButton, getProductIconTileStyle, ProductIcon, SidebarContainer, UserAccountMenu,
} from '@mia-platform-internal/sidebar-widget';
import OrganizationSelector from './OrganizationSelector';
import BackgroundTasksIndicator from './BackgroundTasksIndicator';
import { useAuth } from '../context/AuthContext';
import { getOrganizationConfiguration } from '../services/organizationConfigService';
import { getThemeMode, setThemeMode } from '../utils/themeMode';
import { devLog } from '../utils/devLog';
import config from '../config';
import useChatFeature from '../hooks/useChatFeature';
import useSandbox from '../hooks/useSandbox';
import { includedConnections } from './sandbox/connections';
import { SearchTrigger } from './search/SearchContext';
import NotificationsBell from './collab/NotificationsBell';
import LeaveNotePrompt from './collab/LeaveNotePrompt';
import { ParticipantMark } from './presence/Presence';
import useLiveNow from '../hooks/useLiveNow';
import useActivityBadge from '../hooks/useActivityBadge';
import useWatchNotifications from '../hooks/useWatchNotifications';
import useSandboxAdmin, { SANDBOX_ADMIN_URL } from '../hooks/useSandboxAdmin';
import { FLOW_DOCS_URL } from '../constants/helpLinks';
import { sidebarLiveProjects } from '../utils/liveModel';
import { activityBadgeText } from '../utils/activityBadge';
import './FlowSidebar.css';

/**
 * Flow's own thin wrapper over the new, app-agnostic
 * @mia-platform-internal/sidebar-widget package (ported from
 * catalog-website's real Sidebar.tsx/SidebarContainer.tsx/ThemeSwitcher —
 * same generic shell + the same real app-switcher and account-menu
 * structure catalog itself uses). This is the FIRST integration of that
 * shared package; ai-foundry-website and catalog-website are planned to
 * grow their own equivalent thin wrappers on top of the SAME package later,
 * not this file.
 *
 * `AppSwitcher`/`UserAccountMenu` are the package's generic, prop-driven
 * versions of catalog's own inlined app-switcher and account-popover — Flow
 * supplies its own org-config source, product icon map, theme mechanism
 * (utils/themeMode.js — a richer multi-named-theme system than catalog's
 * own 3-state one, so only the simple light/dark toggle is exposed here,
 * matching what this app's account menu always showed) and auth (useAuth).
 *
 * First-pass integration — same props contract as the previous
 * NavigationSidebar.jsx (drop-in replacement in App.jsx), covering the core
 * nav: logo, org selector, app switcher, menu items, background tasks +
 * user avatar/theme/sign-out footer, collapse. Deliberately NOT ported from
 * NavigationSidebar.jsx, by explicit decision (not just deferred): the
 * "no Git connection" popup (was already dead code even there — its trigger
 * logic was never implemented, `hasGitConnection` was a received prop
 * nothing ever read); and the per-item playbook badge on the Chat nav entry
 * (driven by a legacy `activePlaybookChanged` window event today's real
 * chat, VfsChatPanel, never dispatches to — porting it as-is would just be a
 * badge that never lights up).
 *
 * Mobile (< 767px, matching Mobile.css's own breakpoint): this is an
 * off-canvas drawer (`mobileOpen`/`onMobileClose`, own wrapper + backdrop
 * below — the shared `SidebarContainer` shell has no responsive behavior of
 * its own, see its source), opened via App.jsx's hamburger button, not the
 * always-visible icon rail the initial migration deliberately left in place
 * at every width down to phone size (see FlowSidebar.css's 900px block,
 * still governing the 768–900px tablet range).
 *
 * App-switcher icons come from the widget's own `ProductIcon` — catalog's
 * real per-product logo assets, shipped once in the widget rather than
 * duplicated per consuming app (see sidebar-widget/src/ProductIcon.jsx).
 * Nav-menu icons are plain `@phosphor-icons/react` regular-weight icons at
 * size 16 — the exact style catalog's own Sidebar.tsx uses for its menu
 * items — not this app's old custom "cyberpunk" Icon set.
 */
const toTitleCase = (key) => key.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Local-dev-only stand-in for `getOrganizationConfiguration()`'s real product
 * list. `/organization-configuration` proxies to the real
 * https://dev.home.gcp.mia-platform.eu authz backend (see mia-flow-bff's
 * authz.js) and can 403 there for a given org/tenant even with a valid
 * session — a real backend-permission gap, not something this app can fix.
 * Without this fallback the app-switcher silently never renders in that
 * case (AppSwitcher returns null with no products), which made it
 * impossible to visually verify the ported dropdown styling locally.
 */
const DEV_FALLBACK_PRODUCTS = {
  home: { enabled: true, title: 'Homepage', websiteBaseUrl: 'https://mia-platform.eu' },
  'ai-foundry': { enabled: true, title: 'AI Foundry', websiteBaseUrl: 'https://ai-foundry.mia-platform.eu' },
  console: { enabled: true, title: 'Console', websiteBaseUrl: 'https://console.mia-platform.eu' },
  flow: { enabled: true, title: 'Flow', websiteBaseUrl: 'https://flow.mia-platform.eu' },
  p4samd: { enabled: true, title: 'P4SaMD', websiteBaseUrl: 'https://p4samd.mia-platform.eu' },
};

/** The Suite products Flow's own configuration names: the switcher stays when the organization configuration is unavailable. */
const configuredProducts = () => {
  const entries = [
    ['console', 'Console', config.consoleNextWebUrl || config.consoleBaseUrl],
    ['ai-foundry', 'AI Foundry', config.aiFoundryBaseUrl],
  ].filter(([, , url]) => url);
  return entries.length
    ? Object.fromEntries(entries.map(([key, title, url]) => [key, { enabled: true, title, websiteBaseUrl: url }]))
    : null;
};

/** The app's real logo (public/flow.svg), not a hand-drawn stand-in. */
/** Footer button to the Sandbox admin app (vfs-service), for sandbox admins only; same look as the widget's "?". */
const SandboxAdminButton = () => (
  <Tooltip placement="right" title="Sandbox admin">
    <button
      aria-label="Sandbox admin"
      className="sbw-docs-button flow-sidebar-admin"
      onClick={() => window.open(SANDBOX_ADMIN_URL, '_blank', 'noopener,noreferrer')}
      type="button"
    >
      <ShieldCheckIcon size={24} />
    </button>
  </Tooltip>
);

const FlowLogo = () => <img alt="" height={24} src="/flow/flow.svg" width={24} />;

/** The logo and the name lead to Flow's Home (a link: middle-click opens a tab; a click navigates in the app). */
const HomeLink = ({ children, className, onHome, label }) => (
  <a
    aria-label={label}
    className={`flow-sidebar-home-link${className ? ` ${className}` : ''}`}
    href="/flow/"
    onClick={(event) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) { return; }
      event.preventDefault();
      onHome();
    }}
  >
    {children}
  </a>
);

const FlowSidebar = ({
  currentPath,
  onNavigate,
  selectedTenant = null,
  isCollapsed = false,
  user = null,
  onToggleCollapse,
  mobileOpen = false,
  onMobileClose,
}) => {
  const { logout } = useAuth();
  const goHome = () => { onNavigate('/'); onMobileClose?.(); };
  // Chat not in the person's plan: no Chat entry at all.
  const [chatOn, , chatAvailable] = useChatFeature();
  // Sandbox-first: the sandbox's state next to its menu item (never wakes it more than the overview already does).
  const { sandbox, error: sandboxError } = useSandbox({ enabled: Boolean(config.sandboxFirst), intervalMs: 30000 });
  const sandboxBadge = !config.sandboxFirst ? null
    : sandbox?.state === 'running' ? 'Running' : sandboxError?.status === 401 ? 'Signed out' : sandbox ? 'Asleep' : null;
  // How many paid add-ons the person's plan includes, next to Connections.
  const connectionsCount = config.sandboxFirst ? includedConnections(sandbox) : 0;
  // Collaboration: the shared projects someone (or an AI agent) is working in right now, under Projects (the same
  // `/api/v1/live` poll as Home's "Happening now"), and how much is new in Activity (inbox + AI tool sessions).
  const live = sidebarLiveProjects(useLiveNow(), { max: 3 });
  // Projects watched from the kanban's live bar: say when someone starts editing there (the sidebar is always on screen).
  useWatchNotifications((projectId) => onNavigate(`/open/${encodeURIComponent(projectId)}`));
  const activityNew = activityBadgeText(useActivityBadge({ onActivityPage: currentPath.startsWith('/history') }));
  // Sandbox admins get a shortcut to the admin app (asked once per page, hidden until the gateway says yes).
  const sandboxAdmin = useSandboxAdmin(user?.sub || user?.id || user?.email, { enabled: Boolean(config.sandboxFirst || config.vfsServiceUrl) });
  const [themeValue, setThemeValue] = useState(getThemeMode);
  const [orgProducts, setOrgProducts] = useState(null);
  useEffect(() => {
    let active = true;
    getOrganizationConfiguration()
      .then((loaded) => { if (active) setOrgProducts(loaded?.products || null); })
      .catch((error) => {
        devLog('🔀 App switcher: organization configuration unavailable', error?.message);
        if (active && import.meta.env.DEV) {
          devLog('🔀 App switcher: using local-dev fallback product list');
          setOrgProducts(DEV_FALLBACK_PRODUCTS);
        }
      });
    return () => { active = false; };
  }, []);

  const switcherProducts = (orgProducts || configuredProducts()) && Object.entries(orgProducts || configuredProducts())
    .filter(([, product]) => product?.enabled && product?.websiteBaseUrl)
    .map(([key, product]) => ({
      key,
      label: product.title || (key === 'home' ? 'Homepage' : toTitleCase(key)),
      icon: <ProductIcon productKey={key} />,
      href: product.websiteBaseUrl,
      ...getProductIconTileStyle(key),
    }));

  const isActive = (path) => {
    if (path === '/') { return currentPath === '/'; }
    if (path === '/chat') {
      if (currentPath.startsWith('/features/chat')) { return true; }
      // Sandbox-first: a project is not "chat" any more, it belongs to Projects.
      if (config.sandboxFirst && currentPath.startsWith('/code')) { return false; }
      return currentPath.startsWith('/chat') || currentPath.startsWith('/code') || currentPath.startsWith('/agents');
    }
    if (path === '/projects' && config.sandboxFirst && currentPath.startsWith('/code')) { return true; }
    return currentPath.startsWith(path);
  };

  const openNewChat = () => {
    const newId = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    onNavigate(`/chat?conversationId=${newId}`);
  };

  // Sandbox-first (docs/SANDBOX_FIRST.md): the first item is always Home, whatever the settings (with Chat on it
  // starts with the prompt, the sandbox below; see pages/SandboxHome.jsx), with the sandbox's state next to it. Chat
  // and Brain are features under their own group; Chat leads to its Features page until it is turned on.
  const NAV_ITEMS = config.sandboxFirst ? [
    // Chat off: Home is the sandbox overview, so it carries the sandbox's state. Chat on: Home leads with the prompt
    // and the full sandbox overview stays one click away under Sandbox.
    { key: '/', label: 'Home', badge: chatOn ? null : sandboxBadge, badgeTone: !chatOn && sandboxBadge === 'Running' ? 'on' : null, icon: <HouseIcon size={16} /> },
    ...(chatOn ? [{ key: '/sandbox', label: 'Sandbox', badge: sandboxBadge, badgeTone: sandboxBadge === 'Running' ? 'on' : null, icon: <CpuIcon size={16} /> }] : []),
    { key: '/projects', label: 'Projects', icon: <FolderIcon size={16} /> },
    // Workspaces (vfs-service docs/workspaces.md): groups of projects with their own members and brain.
    { key: '/workspaces', label: 'Workspaces', icon: <UsersThreeIcon size={16} /> },
    { key: '/history', label: 'Activity', badge: activityNew, badgeTone: activityNew ? 'new' : null, icon: <ClockCounterClockwiseIcon size={16} /> },
    ...(chatAvailable ? [{ key: '/chat', group: 'Features', label: 'Chat', badge: chatOn ? null : 'Turn on', icon: <RobotIcon size={16} />,
      onClick: () => (chatOn ? openNewChat() : onNavigate('/features/chat')) }] : []),
    { key: '/brain', group: 'Features', label: 'Brain', icon: <DiamondsFourIcon size={16} /> },
    { key: '/features/connections', group: 'Features', label: 'Connections', badge: connectionsCount ? String(connectionsCount) : null,
      icon: <PlugsConnectedIcon size={16} /> },
  ] : [
    { key: '/', label: 'Home', icon: <HouseIcon size={16} /> },
    { key: '/chat', label: 'Chat', icon: <RobotIcon size={16} />, onClick: openNewChat },
    { key: '/projects', label: 'Projects', icon: <FolderIcon size={16} /> },
    { key: '/history', label: 'Activity', badge: activityNew, badgeTone: activityNew ? 'new' : null, icon: <ClockCounterClockwiseIcon size={16} /> },
    { key: '/brain', label: 'Brain', icon: <DiamondsFourIcon size={16} /> },
  ];

  const toMenuItem = (item) => ({
    key: item.key,
    label: item.badge
      ? <span className="flow-sidebar-item-with-badge">{item.label}<span className={`flow-sidebar-badge${item.badgeTone ? ` ${item.badgeTone}` : ''}`}>{item.badge}</span></span>
      : item.label,
    icon: item.key === '/projects' && isCollapsed && live.length > 0
      ? <span className="flow-sidebar-icon-live" title={`Live now: ${live.map((p) => p.name).join(', ')}`}>{item.icon}<span aria-hidden className="flow-sidebar-live-dot" /></span>
      : item.icon,
  });
  // LIVE NOW, right under Projects (expanded sidebar only): each row opens that project.
  const liveGroup = !isCollapsed && live.length > 0 ? [{
    type: 'group',
    key: 'group:live',
    label: <span className="flow-sidebar-live-title"><span aria-hidden className="flow-sidebar-live-dot" />Live now</span>,
    children: live.map((project) => ({
      key: `live:${project.projectId}`,
      label: (
        <span className="flow-sidebar-live-row" data-testid="sidebar-live-row" title={project.label}>
          <span className="flow-sidebar-live-name">{project.name}</span>
          <span className="flow-sidebar-live-marks">
            {project.marks.map((mark) => (
              <ParticipantMark agent={mark.agent} idle={mark.idle} initials={mark.initials} key={mark.key} label={mark.label} paused={mark.paused} size={18} slot={mark.slot} />
            ))}
            {project.more > 0 && <span className="flow-sidebar-live-more">+{project.more}</span>}
          </span>
        </span>
      ),
    })),
  }] : [];
  const groups = [...new Set(NAV_ITEMS.filter((item) => item.group).map((item) => item.group))];
  const menuItems = [
    ...NAV_ITEMS.filter((item) => !item.group).flatMap((item) => (item.key === '/projects' ? [toMenuItem(item), ...liveGroup] : [toMenuItem(item)])),
    ...groups.map((group) => ({
      type: 'group',
      key: `group:${group}`,
      label: isCollapsed ? null : group,
      children: NAV_ITEMS.filter((item) => item.group === group).map(toMenuItem),
    })),
  ];

  const selectedKeys = [NAV_ITEMS.find((item) => isActive(item.key))?.key].filter(Boolean);

  return (
    <>
      {mobileOpen && (
        <div className="flow-sidebar-mobile-backdrop" onClick={onMobileClose} />
      )}
      <div className={`flow-sidebar-mobile-wrap${mobileOpen ? ' mobile-open' : ''}`}>
        <SidebarContainer
          collapsed={isCollapsed}
          extra={<AppSwitcher products={switcherProducts} />}
          footer={(collapsed) => (
            <div className="flow-sidebar-footer">
              {/* Its own row, stacked above the account menu — sharing one flex
                  row with `justify-content: space-between` (the old layout)
                  meant the account menu's own position shifted left/right
                  whenever this indicator appeared/disappeared, since it's the
                  only other flex item. Keeping it in a separate div means the
                  avatar never moves regardless of whether tasks are running. */}
              <div className="flow-sidebar-background-tasks-row">
                <BackgroundTasksIndicator
                  collapsed={collapsed}
                  tenantId={selectedTenant?.id || selectedTenant?.tenantId}
                  userId={user?.sub || user?.id || user?.email}
                />
              </div>
              <div className={`flow-sidebar-footer-row${collapsed ? ' collapsed' : ''}`}>
                <UserAccountMenu
                  extra={(sandboxAdmin || !config.sandboxFirst) ? (
                    <>
                      {sandboxAdmin && <SandboxAdminButton />}
                      {!config.sandboxFirst && <DocumentationButton href={FLOW_DOCS_URL} />}
                    </>
                  ) : null}
                  onSignOut={logout}
                  onThemeChange={(mode) => { setThemeMode(mode); setThemeValue(mode); }}
                  placement={collapsed ? 'bottomRight' : 'topLeft'}
                  themeValue={themeValue}
                  user={user}
                />
                {/* Mentions in shared projects (collaboration); hidden where the sandbox gateway has no inbox. */}
                <NotificationsBell collapsed={collapsed} onNavigate={(path) => { onNavigate(path); onMobileClose?.(); }} />
                <LeaveNotePrompt />
                {config.sandboxFirst && (
                  <button
                    aria-label="Settings"
                    className={`flow-sidebar-settings${currentPath.startsWith('/settings') ? ' active' : ''}`}
                    onClick={() => { onNavigate('/settings'); onMobileClose?.(); }}
                    title="Settings"
                    type="button"
                  >
                    <GearIcon size={20} />
                  </button>
                )}
              </div>
            </div>
          )}
          logo={<HomeLink label="Flow Home" onHome={goHome}><FlowLogo /></HomeLink>}
          menuItems={menuItems}
          menuPrefix={(
            <>
              {/* Global search (⌘K): a box at the top, an icon when collapsed (search/SearchContext.jsx). */}
              <SearchTrigger compact={isCollapsed} />
              {!isCollapsed && (
                <div className="flow-sidebar-org-row">
                  <OrganizationSelector />
                </div>
              )}
            </>
          )}
          onCollapsedChange={onToggleCollapse}
          onMenuClick={({ key }) => {
            if (String(key).startsWith('live:')) {
              onNavigate(`/open/${encodeURIComponent(String(key).slice('live:'.length))}`);
              onMobileClose?.();
              return;
            }
            const item = NAV_ITEMS.find((i) => i.key === key);
            if (item?.onClick) { item.onClick(); } else { onNavigate(key); }
            onMobileClose?.();
          }}
          selectedKeys={selectedKeys}
          title={<span className="flow-sidebar-title"><HomeLink onHome={goHome}>Flow</HomeLink>{config.flowVersion && <span className="flow-sidebar-version" data-testid="flow-version" title={`Flow ${config.flowVersion}`}>{config.flowVersion}</span>}</span>}
          width={248}
        />
      </div>
    </>
  );
};

FlowSidebar.propTypes = {
  currentPath: PropTypes.string.isRequired,
  onNavigate: PropTypes.func.isRequired,
  selectedTenant: PropTypes.object,
  isCollapsed: PropTypes.bool,
  user: PropTypes.object,
  onToggleCollapse: PropTypes.func,
  mobileOpen: PropTypes.bool,
  onMobileClose: PropTypes.func,
};

export default FlowSidebar;
